import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQueries } from '../../app/ApiClientContext';
import { useToast } from '../../app/ToastProvider';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { buildShortUrl, originForLinks } from '../../lib/shortUrl';
import { randomAlias } from '../../lib/randomAlias';
import { copyToClipboard } from '../../lib/clipboard';

export function CreateLinkForm() {
  const $api = useApiQueries();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [urlTo, setUrlTo] = useState('');
  const [alias, setAlias] = useState(() => randomAlias(6));
  const [force, setForce] = useState(false);
  const [conflict, setConflict] = useState(false);

  const createMut = $api.useMutation('put', '/api/v2/link');

  const origin = originForLinks();
  const disabled = !urlTo || createMut.isPending;

  async function submit(copy: boolean) {
    setConflict(false);
    const url_from = buildShortUrl(origin, alias);
    const result = await createMut.mutateAsync({
      body: { url_from, url_to: urlTo, force },
    } as Parameters<typeof createMut.mutateAsync>[0]);
    const created = result as { url_from: string };
    toast.push('success', `Ссылка создана: ${created.url_from}`);
    await queryClient.invalidateQueries({ queryKey: ['get', '/api/v2/link'] });
    if (copy) {
      try {
        await copyToClipboard(created.url_from);
        toast.push('info', 'Скопировано в буфер');
      } catch {
        toast.push('error', 'Не удалось скопировать');
      }
    }
    setUrlTo('');
    setAlias(randomAlias(6));
    setForce(false);
  }

  function handle(copy: boolean) {
    return (e: React.MouseEvent | React.FormEvent) => {
      e.preventDefault();
      void submit(copy).catch((err) => {
        if (err?.response?.status === 409 || /already exists/i.test(String(err?.message))) {
          setConflict(true);
          return;
        }
        toast.push('error', 'Не удалось создать ссылку');
      });
    };
  }

  return (
    <form className="space-y-3" onSubmit={handle(false)}>
      <Input
        label="Ссылка, которую нужно сократить"
        type="url"
        required
        placeholder="https://dyakov.space/"
        value={urlTo}
        onChange={(e) => setUrlTo(e.target.value)}
      />
      <Input
        label="Имя ссылки"
        prefix={`${origin}/`}
        value={alias}
        pattern="[A-Za-z0-9._-]+"
        onChange={(e) => setAlias(e.target.value)}
      />
      {conflict && (
        <div className="text-sm text-[#a4322a] flex items-center gap-2">
          <span>Этот алиас уже занят.</span>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            Перезаписать
          </label>
        </div>
      )}
      <div className="flex gap-2">
        <Button type="submit" disabled={disabled}>
          Создать
        </Button>
        <Button type="button" variant="secondary" disabled={disabled} onClick={handle(true)}>
          Создать и скопировать
        </Button>
      </div>
    </form>
  );
}
